import { FirebaseError } from 'firebase/app'
import { BridgePublishableClientError } from './bridgePublishableClient'
import { raceAbort, sleepMs } from './bridgePolicyCreateRetry'

export const BRIDGE_TRANSPORT_RETRY_BUDGET_MS = 15_000
export const BRIDGE_TRANSPORT_RETRY_INTERVAL_MS = 1_000
export const BRIDGE_CALLABLE_TIMEOUT_MESSAGE = 'bridge_timeout'

function isRetriablePublishableHttpStatus(statusCode: number | undefined): boolean {
  if (statusCode === 0) {
    return true
  }
  return statusCode === 408 || statusCode === 429 || (statusCode ?? 0) >= 500
}

function isTimeoutMessage(message: string): boolean {
  return /\btimeout\b/i.test(message)
}

export function isBridgePublishableTransportError(error: unknown): boolean {
  if (error instanceof BridgePublishableClientError) {
    if (error.statusCode === undefined) {
      return isTimeoutMessage(error.message)
    }
    return isRetriablePublishableHttpStatus(error.statusCode) || isTimeoutMessage(error.message)
  }
  if (error instanceof Error) {
    if (error.name === 'AbortError' || error.name === 'TimeoutError') {
      return true
    }
    if (isTimeoutMessage(error.message)) {
      return true
    }
  }
  if (error instanceof TypeError) {
    return true
  }
  return false
}

export function isBridgeCallableTransportError(error: unknown): boolean {
  if (error instanceof FirebaseError) {
    return (
      error.code === 'functions/unavailable' && error.message === BRIDGE_CALLABLE_TIMEOUT_MESSAGE
    )
  }
  return isBridgePublishableTransportError(error)
}

export function isBridgeTransportStepErrorSuppressed(error: unknown): boolean {
  if (!(error instanceof Error)) {
    return false
  }
  if (isBridgeCallableTransportError(error)) {
    return true
  }
  return isTimeoutMessage(error.message)
}

export function isBridgeVerifyAbortError(error: unknown): boolean {
  return (
    typeof error === 'object' &&
    error !== null &&
    'name' in error &&
    (error as { name: unknown }).name === 'AbortError'
  )
}

export function isInsuranceVerifyWaitErrorSuppressed(error: unknown): boolean {
  if (isBridgeVerifyAbortError(error)) {
    return true
  }
  return isBridgeTransportStepErrorSuppressed(error)
}

export function isInsuranceVerifyAttemptCurrent(
  signal: AbortSignal,
  actionGeneration: number,
  currentGeneration: number,
): boolean {
  return !signal.aborted && actionGeneration === currentGeneration
}

function readRetryExternalId(error: unknown): string | undefined {
  if (!(error instanceof FirebaseError)) {
    return undefined
  }
  const details = (error as FirebaseError & { details?: { externalId?: unknown } }).details
  return typeof details?.externalId === 'string' && details.externalId.length > 0
    ? details.externalId
    : undefined
}

function bridgeVerifyAbortError(): DOMException {
  return new DOMException('Bridge policy create cancelled', 'AbortError')
}

export type BridgeTransportRetryOptions = {
  budgetMs?: number
  intervalMs?: number
  now?: () => number
  sleep?: (ms: number, signal?: AbortSignal) => Promise<void>
  signal?: AbortSignal
}

export async function withBridgeTransportRetry<T>(
  fn: (externalId?: string) => Promise<T>,
  options?: BridgeTransportRetryOptions,
): Promise<T> {
  const budgetMs = options?.budgetMs ?? BRIDGE_TRANSPORT_RETRY_BUDGET_MS
  const intervalMs = options?.intervalMs ?? BRIDGE_TRANSPORT_RETRY_INTERVAL_MS
  const now = options?.now ?? (() => Date.now())
  const sleep = options?.sleep ?? ((ms: number, signal?: AbortSignal) => sleepMs(ms, signal))
  const signal = options?.signal

  const startedAt = now()
  let externalId: string | undefined

  while (true) {
    if (signal?.aborted) {
      throw bridgeVerifyAbortError()
    }
    try {
      return await raceAbort(fn(externalId), signal)
    } catch (error) {
      if (signal?.aborted) {
        throw isBridgeVerifyAbortError(error) ? error : bridgeVerifyAbortError()
      }
      if (!isBridgeCallableTransportError(error)) {
        throw error
      }
      const nextExternalId = readRetryExternalId(error)
      if (nextExternalId) {
        externalId = nextExternalId
      }
      if (now() - startedAt >= budgetMs) {
        throw error
      }
      await sleep(intervalMs, signal)
      if (signal?.aborted) {
        throw bridgeVerifyAbortError()
      }
    }
  }
}
